import { getRequest, postRequest, SuperAdminBaseUrl, updateRequest } from '@/System/Store/endpoints';
import { formatDate, setSnackbar, processCertificatesArray } from '@/helpers';
import { clearExpertiseBreakdownCache } from '@composables/useExpertiseBreakdown';
import SwedishOrgNr from '@validators/swedish-org';

const initialState = () => ({
    users: [],
    companies: [],
    competences: [],
    competences_categories: [],
    departments: [],
    department_users: [],
    user_departments: [],
    selected_department_id: null,
    department_certificates: [],
    department_disconnected_users: [],
});

const state = initialState();

const mutations = {
    SET_USERS: (state, array) => (state.users = array),
    SET_COMPANIES: (state, array) => (state.companies = array),
    SET_COMPETENCES: (state, array) => (state.competences = array),
    SET_COMPETENCES_CATEGORIES: (state, array) => (state.competences_categories = array),
    SET_DEPARTMENTS: (state, array) => (state.departments = array),
    SET_DEPARTMENT_USERS: (state, array) => (state.department_users = array),
    SET_DEPARTMENT_DISCONNECTED_USERS: (state, array) => (state.department_disconnected_users = array),
    SET_USER_DEPARTMENTS: (state, array) => (state.user_departments = array),
    SET_SELECTED_DEPARTMENT_ID: (state, id) => (state.selected_department_id = id),
    SET_DEPARTMENT_FIELD: (state, { id, field, value }) => {
        const department = state.departments.find((d) => d.id === id);
        if (!department) return false;

        department[field] = value;
    },
    TOGGLE_DISCONNECTED_USER: (state, { id }) => {
        const disconnected_user = state.department_disconnected_users.find((d) => d.id === id);
        if (!disconnected_user) return false;
        disconnected_user.selected = !disconnected_user.selected;
    },
    TOGGLE_ALL_DISCONNECTED_USER: (state, { selectedStatus }) => {
        state.department_disconnected_users.forEach((d) => {
            d.selected = selectedStatus;
        });
    },
    SET_DEPARTMENT_CERTIFICATES: (state, array) => (state.department_certificates = array),
    RESET_STATE: (state) => Object.assign(state, initialState()),
};

const actions = {
    GET_USERS({ commit }) {
        getRequest(`${SuperAdminBaseUrl}/users`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_USERS', response.data);
            }
        });
    },
    GET_COMPANIES({ commit }) {
        getRequest(`${SuperAdminBaseUrl}/companies`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_COMPANIES', response.data);
            }
        });
    },
    async GET_DEPARTMENTS({ commit }) {
        return getRequest(`${SuperAdminBaseUrl}/departments`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_DEPARTMENTS', response.data);
            }
        });
    },
    async LOCK_DEPARTMENT({ dispatch }, data) {
        if (!data.id) return false;
        const payload = {};

        if (data.date) {
            payload.date = data.date;
        }

        return await postRequest(`${SuperAdminBaseUrl}/departments/${data.id}/access`, payload).then((response) => {
            const snackbar = setSnackbar(response);
            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });

            if (response.status === 'Success') {
                return response.data;
            }

            return false;
        });
    },
    async UNLOCK_DEPARTMENT({ dispatch }, id) {
        return await updateRequest(`${SuperAdminBaseUrl}/departments/${id}/access`).then((response) => {
            const snackbar = setSnackbar(response);
            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });

            return response.status === 'Success';
        });
    },
    GET_COMPETENCES({ commit }) {
        getRequest(`${SuperAdminBaseUrl}/competences`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_COMPETENCES', response.data);
            }
        });
    },
    GET_COMPETENCES_CATEGORIES({ commit }) {
        getRequest(`${SuperAdminBaseUrl}/competences/categories`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_COMPETENCES_CATEGORIES', response.data);
            }
        });
    },
    GET_DEPARTMENT_DISCONNECTED_USERS({ commit }) {
        getRequest(`${SuperAdminBaseUrl}/departments/${state.selected_department_id}/disconnected_users`).then(
            (response) => {
                if (response.status === 'Success') {
                    response.data.forEach((user) => {
                        user['selected'] = false;
                    });
                    commit('SET_DEPARTMENT_DISCONNECTED_USERS', response.data);
                }
            }
        );
    },
    GET_DEPARTMENT_USERS({ commit }) {
        getRequest(`${SuperAdminBaseUrl}/departments/${state.selected_department_id}/users`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_DEPARTMENT_USERS', response.data);
            }
        });
    },
    GET_USER_DEPARTMENTS({ commit }, id) {
        getRequest(`${SuperAdminBaseUrl}/users/${id}/departments`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_USER_DEPARTMENTS', response.data);
            }
        });
    },
    async GET_DEPARTMENT_CERTIFICATES({ commit }, id) {
        return getRequest(`${SuperAdminBaseUrl}/departments/${id}/certificates`).then((response) => {
            if (response.status === 'Success') {
                commit('SET_DEPARTMENT_CERTIFICATES', processCertificatesArray(response.data).certificates);
            }
        });
    },
    CREATE_CERTIFICATE: async ({ commit, state, rootGetters, dispatch }) => {
        const payload = rootGetters['CreateCertificateStore/GET_PAYLOAD'];
        const id = state.selected_department_id;

        if (!id) {
            let snackbar = setSnackbar('warning');
            snackbar.text = 'Please select a department';
            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
            return false;
        }

        commit('GlobalStore/SET_SAVE_STATUS', 'pending', { root: true });

        return await postRequest(`${SuperAdminBaseUrl}/departments/${id}/certificates`, payload)
            .then((response) => {
                let snackbar = setSnackbar(response, true);
                dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
                commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });

                // Warning means some certificates were created and some were skipped.
                // Treating it as a failure would send the user back to re-submit
                // certificates that already exist; the snackbar carries the detail.
                const created = response.status !== 'Error';

                if (created) {
                    clearExpertiseBreakdownCache();
                }

                return created;
            })
            .catch(() => {
                commit('GlobalStore/SET_SAVE_STATUS', 'Error', { root: true });
                return false;
            });
    },
    UPLOADED_CERTIFICATES_FILE: async ({ commit, state, rootGetters, dispatch }) => {
        const payload = rootGetters['CreateCertificateStore/GET_PAYLOAD'];
        const id = state.selected_department_id;

        const formData = new FormData();
        const blob = await fetch(payload.file).then((res) => res.blob());

        formData.append('file', blob);
        formData.append('department_id', id);
        // GET_PAYLOAD drops competence_id for a group and moves it to group_competence_id.
        formData.append('competence_id', payload.competence_id ?? payload.group_competence_id);
        formData.append('issuer_id', payload.issuer_id);

        (payload.competences ?? []).forEach((row) => formData.append('member_ids[]', row.competence_id));

        return await postRequest(`${SuperAdminBaseUrl}/departments/${id}/certificates/prepare`, formData, {
            'Content-Type': 'multipart/form-data',
        })
            .then((response) => {
                if (response.data.length > 0 && response.status === 'Success') {
                    response.data.forEach((row) => {
                        row.user.national_identity_number = row.user.national_identity_number_raw;
                        row.issued_at = formatDate(row.issued_at);

                        delete row.user.national_identity_number_raw;

                        if (row.client) {
                            row.client = {
                                ...row.client,
                                valid: SwedishOrgNr.validate(row.client.organization_number) && row.client.name,
                            };
                        }
                    });

                    commit('CreateCertificateStore/SET_UPLOADED_CERTIFICATES', response.data, { root: true });
                }

                if (response.status !== 'Success') {
                    dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                }

                return response.status === 'Success';
            })
            .catch((error) => {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(error), { root: true });
                commit('GlobalStore/SET_SAVE_STATUS', 'Error', { root: true });
            });
    },
    CREATE_COMPETENCE: async ({ dispatch }, payload) => {
        return await postRequest(`${SuperAdminBaseUrl}/competences`, payload)
            .then((response) => {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                return response.status === 'Success';
            })
            .catch(() => {
                return false;
            });
    },
    CREATE_COMPETENCE_CATEGORY: async ({ dispatch }, payload) => {
        return await postRequest(`${SuperAdminBaseUrl}/competences/categories`, payload)
            .then((response) => {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                return response.status === 'Success';
            })
            .catch(() => {
                return false;
            });
    },
    UPDATE_COMPETENCE: async ({ dispatch }, payload) => {
        return await updateRequest(`${SuperAdminBaseUrl}/competences/${payload.id}`, payload)
            .then((response) => {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                return response.status === 'Success';
            })
            .catch(() => {
                return false;
            });
    },
    CONNECT_USERS: async ({ dispatch }) => {
        const id = state.selected_department_id;

        const payload = {
            user_ids: [],
        };
        state.department_disconnected_users.forEach((item) => {
            if (item.selected) {
                payload.user_ids.push(item.id);
            }
        });

        return await postRequest(`${SuperAdminBaseUrl}/departments/${id}/connect_users`, payload)
            .then((response) => {
                let snackbar = setSnackbar('info');
                snackbar.text = `Valda användare har kopplats som medarbetere`;
                dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
                return response.status === 'Success';
            })
            .catch(() => {});
    },
    RESET_STATE: (state) => Object.assign(state, initialState()),
};

const getters = {
    GET_USERS: (state) => state.users,
    GET_COMPANIES: (state) => state.companies,
    GET_DEPARTMENTS: (state) => state.departments,
    GET_COMPETENCES: (state) => state.competences,
    GET_COMPETENCES_CATEGORIES: (state) => state.competences_categories,
    GET_DEPARTMENT_USERS: (state) => state.department_users,
    GET_USER_DEPARTMENTS: (state) => state.user_departments,
    GET_DEPARTMENT: (state) => (id) => state.departments.find((department) => department.id === id),
    GET_DEPARTMENT_CERTIFICATES: (state) => state.department_certificates,
    GET_DEPARTMENT_DISCONNECTED_USERS: (state) => state.department_disconnected_users,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
